/**
 * Where things go on a plan's graph, and what state each piece is in.
 *
 * A plan is drawn as waves: wave 1 is every task with nothing to wait for,
 * wave 2 the tasks that only wait on wave 1, and so on — the order the work
 * can actually happen in. Each wave is a band (as the Architecture map draws
 * its layers), toned by how far along it is, and the tasks that can run side
 * by side stack inside it. The critical path leads each stack, so it runs
 * along the top of the picture as one line.
 *
 * A long chain of waves is wider than any pane: drawn as one row it shrinks
 * to a strip with a lot of nothing above and below. Given the frame it has
 * to fill, the layout breaks the waves into as many rows as make the picture
 * largest, reading left to right and then down, like text.
 *
 * Pure and separate from the view so it can be tested without a DOM.
 */
import { type ArtTone, BAND_HEAD, BAND_PAD, bandAround, type FlowPoint, type FlowRect } from '../flowStyle';

/** A task card: fixed, so a band wraps its cards exactly whatever a card has to say. */
export const PLAN_CARD = { width: 272, height: 116 } as const;
/** Between two cards in a wave. */
export const PLAN_CARD_GAP = 14;
/** Between two waves: the channel the arrows run in. */
export const PLAN_WAVE_GAP = 64;
/** Between two rows of waves, when a long plan wraps. */
export const PLAN_ROW_GAP = 48;
/** How far outside the first and last bands an arrow that goes round them runs. */
export const PLAN_EDGE_MARGIN = 14;
/** No more rows than this: past it the eye loses the order. */
export const PLAN_MAX_ROWS = 3;
/** A second row has to buy at least this much more zoom to be worth the arrows that go round. */
const WRAP_GAIN = 1.15;

export interface PlanGraphTask {
  ref: string;
  dependsOn: readonly string[];
  status?: string;
  outcome?: string;
  estimate?: { complexity?: number };
}

/**
 * What a task is doing, as the graph shows it. `ready` is a task nobody has
 * started whose dependencies are all done: the one to look at next.
 */
export type PlanTaskState = 'done' | 'running' | 'review' | 'blocked' | 'failed' | 'ready' | 'waiting';
export type PlanWaveState = 'done' | 'running' | 'blocked' | 'waiting';
export type PlanEdgeKind = 'satisfied' | 'blocked' | 'critical' | 'running' | 'dependency';

export const TASK_TONE: Record<PlanTaskState, ArtTone> = { done: 'green', running: 'blue', review: 'amber', blocked: 'rose', failed: 'rose', ready: 'slate', waiting: 'slate' };
export const WAVE_TONE: Record<PlanWaveState, ArtTone> = { done: 'green', running: 'blue', blocked: 'rose', waiting: 'slate' };
/** The critical path is violet: no state uses it, and the accent already means "selected" on every canvas. */
export const CRITICAL_TONE: ArtTone = 'violet';

export const TASK_LABEL: Record<PlanTaskState, string> = { done: 'done', running: 'in progress', review: 'in review', blocked: 'blocked', failed: 'failed', ready: 'up next', waiting: 'waiting' };

/** A board status (and the outcome of a finished task) as a graph state, before dependencies are considered. */
export function taskState(status: string | undefined, outcome?: string): Exclude<PlanTaskState, 'ready'> {
  if (status === 'done') return outcome === 'failure' ? 'failed' : 'done';
  if (status === 'blocked') return 'blocked';
  if (status === 'review') return 'review';
  if (status === 'in-progress') return 'running';
  return 'waiting';
}

const inFlight = (s: PlanTaskState) => s === 'running' || s === 'review';
const stuck = (s: PlanTaskState) => s === 'blocked' || s === 'failed';

export interface PlanCardLayout {
  ref: string;
  /** 0-based wave. */
  wave: number;
  x: number;
  y: number;
  state: PlanTaskState;
  critical: boolean;
}

export interface PlanWaveLayout {
  index: number;
  /** Which row of waves it is on; 0 unless the plan wrapped. */
  row: number;
  refs: string[];
  state: PlanWaveState;
  tone: ArtTone;
  title: string;
  description: string;
  rect: FlowRect;
}

export interface PlanEdgeLayout {
  id: string;
  source: string;
  target: string;
  kind: PlanEdgeKind;
  /** On the critical path, whatever colour it ends up. */
  critical: boolean;
  /**
   * The corners of a clear way round, for an edge that would otherwise run
   * behind a card: one that skips a wave goes along the gap between two rows
   * of cards, and one that runs on to a later row of waves turns in the
   * channel between the rows. An edge to the next wave needs none.
   */
  via?: FlowPoint[];
}

export interface PlanGraphLayout {
  cards: PlanCardLayout[];
  waves: PlanWaveLayout[];
  edges: PlanEdgeLayout[];
  rows: number;
  /** Everything drawn, bands included. */
  bounds: FlowRect;
  /** Any task running or in review: the only time anything on the graph moves. */
  inFlight: boolean;
}

/**
 * Wave per task: the longest chain of dependencies under it. A dependency
 * on a task the plan does not have is ignored, and a cycle (which a plan
 * should never have, but a hand-edited one might) is cut at its cheapest
 * point rather than looping: every task still lands in exactly one wave.
 */
export function planWaves(tasks: readonly PlanGraphTask[]): Map<string, number> {
  const known = new Set(tasks.map((t) => t.ref));
  const deps = new Map(tasks.map((t) => [t.ref, [...new Set(t.dependsOn)].filter((d) => known.has(d) && d !== t.ref)]));
  const wave = new Map<string, number>();
  const placeable = (ref: string) => deps.get(ref)!.every((d) => wave.has(d));
  const after = (ref: string) => Math.max(-1, ...deps.get(ref)!.filter((d) => wave.has(d)).map((d) => wave.get(d)!)) + 1;
  while (wave.size < known.size) {
    const open = tasks.filter((t) => !wave.has(t.ref));
    const ready = open.filter((t) => placeable(t.ref));
    if (ready.length) {
      // All of them at once, from the waves as they stood: a task placed in this pass is not a reason to push its sibling later.
      const next = ready.map((t) => [t.ref, after(t.ref)] as const);
      for (const [ref, w] of next) wave.set(ref, w);
      continue;
    }
    // Only cycles are left. Place the task with the fewest unplaced dependencies (first in plan order) and carry on.
    const unplaced = (ref: string) => deps.get(ref)!.filter((d) => !wave.has(d)).length;
    const cut = [...open].sort((a, b) => unplaced(a.ref) - unplaced(b.ref))[0].ref;
    wave.set(cut, after(cut));
  }
  return wave;
}

/** A task and everything up and down stream of it: what a click on its card lights up. */
export function planChain(tasks: readonly PlanGraphTask[], ref: string): Set<string> {
  const up = new Map(tasks.map((t) => [t.ref, t.dependsOn]));
  const down = new Map<string, string[]>();
  for (const t of tasks) for (const d of t.dependsOn) (down.get(d) ?? down.set(d, []).get(d)!).push(t.ref);
  const chain = new Set<string>();
  if (!up.has(ref)) return chain;
  const walk = (from: string, next: (r: string) => readonly string[]) => {
    const queue = [from];
    const seen = new Set([from]);
    while (queue.length) {
      const at = queue.pop()!;
      chain.add(at);
      for (const n of next(at)) {
        if (!up.has(n) || seen.has(n)) continue;
        seen.add(n);
        queue.push(n);
      }
    }
  };
  walk(ref, (r) => up.get(r) ?? []);
  walk(ref, (r) => down.get(r) ?? []);
  return chain;
}

export interface PlanCounts {
  tasks: number;
  done: number;
  /** Running or in review. */
  inProgress: number;
  /** Blocked, or finished as a failure. */
  blocked: number;
  points: number;
  donePoints: number;
}

export function planCounts(tasks: readonly PlanGraphTask[]): PlanCounts {
  const counts: PlanCounts = { tasks: tasks.length, done: 0, inProgress: 0, blocked: 0, points: 0, donePoints: 0 };
  for (const t of tasks) {
    const state = taskState(t.status, t.outcome);
    const points = t.estimate?.complexity ?? 0;
    counts.points += points;
    if (state === 'done') {
      counts.done++;
      counts.donePoints += points;
    } else if (inFlight(state)) counts.inProgress++;
    else if (stuck(state)) counts.blocked++;
  }
  return counts;
}

function waveState(states: readonly PlanTaskState[]): PlanWaveState {
  if (states.some(stuck)) return 'blocked';
  if (states.every((s) => s === 'done')) return 'done';
  // Part done counts as under way: somebody has been here, even if nobody is right now.
  if (states.some((s) => inFlight(s) || s === 'done')) return 'running';
  return 'waiting';
}

function describeWave(states: readonly PlanTaskState[], index: number): string {
  const n = states.length;
  const count = (test: (s: PlanTaskState) => boolean) => states.filter(test).length;
  const done = count((s) => s === 'done');
  const parts: string[] = [];
  const blocked = count(stuck);
  const running = count(inFlight);
  if (blocked) parts.push(`${blocked} blocked`);
  if (running) parts.push(`${running} in progress`);
  if (done === n) return n === 1 ? 'Done' : `All ${n} done`;
  if (done || parts.length) return [`${done} of ${n} done`, ...parts].join(' · ');
  const size = n === 1 ? '1 task' : `${n} tasks side by side`;
  return index === 0 ? size : `${size} · after wave ${index}`;
}

function waveTitle(index: number, total: number): string {
  if (index === 0) return 'Wave 1 · start here';
  return index === total - 1 ? 'Final wave' : `Wave ${index + 1}`;
}

/** How many rows of waves make the picture largest in `frame`; one when there is no frame to fill. */
function chooseRows(stacks: readonly number[], frame: { width: number; height: number } | undefined, maxZoom: number): number {
  if (!frame || frame.width <= 0 || frame.height <= 0) return 1;
  let best = { rows: 1, zoom: 0 };
  for (let rows = 1; rows <= Math.min(PLAN_MAX_ROWS, stacks.length); rows++) {
    const perRow = Math.ceil(stacks.length / rows);
    // A last row with nothing on it is not a row: 4 waves in 3 rows is 2 + 2.
    if (rows > 1 && perRow * (rows - 1) >= stacks.length) continue;
    const width = perRow * (PLAN_CARD.width + BAND_PAD * 2) + (perRow - 1) * PLAN_WAVE_GAP;
    let height = (rows - 1) * PLAN_ROW_GAP;
    for (let r = 0; r < rows; r++) height += BAND_HEAD + BAND_PAD + stackHeight(Math.max(...stacks.slice(r * perRow, (r + 1) * perRow)));
    const zoom = Math.min(maxZoom, frame.width / width, frame.height / height);
    if (zoom > best.zoom * (best.zoom ? WRAP_GAIN : 1)) best = { rows, zoom };
  }
  return best.rows;
}

const stackHeight = (cards: number) => cards * PLAN_CARD.height + Math.max(0, cards - 1) * PLAN_CARD_GAP;

export function layoutPlanGraph(
  tasks: readonly PlanGraphTask[],
  criticalRefs: readonly string[] = [],
  options: { frame?: { width: number; height: number }; maxZoom?: number } = {},
): PlanGraphLayout {
  const waveOf = planWaves(tasks);
  const critical = new Set(criticalRefs);
  const byRef = new Map(tasks.map((t) => [t.ref, t]));
  const planOrder = new Map(tasks.map((t, i) => [t.ref, i]));

  const state = new Map<string, PlanTaskState>();
  for (const t of tasks) {
    const own = taskState(t.status, t.outcome);
    const deps = t.dependsOn.filter((d) => byRef.has(d));
    const clear = deps.every((d) => taskState(byRef.get(d)!.status, byRef.get(d)!.outcome) === 'done');
    state.set(t.ref, own === 'waiting' && clear ? 'ready' : own);
  }

  // Who is in each wave, top to bottom: the critical path first, then whoever
  // hangs off the highest card in the waves before, so the arrows cross less.
  const total = Math.max(0, ...[...waveOf.values()].map((w) => w + 1));
  const members: string[][] = Array.from({ length: total }, () => []);
  for (const t of tasks) members[waveOf.get(t.ref)!].push(t.ref);
  const slot = new Map<string, number>();
  for (const refs of members) {
    const pull = (ref: string) => {
      const above = byRef.get(ref)!.dependsOn.filter((d) => slot.has(d)).map((d) => slot.get(d)!);
      return above.length ? above.reduce((a, b) => a + b, 0) / above.length : Number.MAX_SAFE_INTEGER;
    };
    refs.sort((a, b) => Number(critical.has(b)) - Number(critical.has(a)) || pull(a) - pull(b) || planOrder.get(a)! - planOrder.get(b)!);
    refs.forEach((ref, i) => slot.set(ref, i));
  }

  const rows = chooseRows(members.map((m) => m.length), options.frame, options.maxZoom ?? 1);
  const perRow = Math.max(1, Math.ceil(total / rows));

  const cards: PlanCardLayout[] = [];
  const waves: PlanWaveLayout[] = [];
  /** The middle of the channel under each row, for the arrows that go round. */
  const channel: number[] = [];
  let top = 0;
  for (let row = 0; row < rows; row++) {
    const inRow = members.slice(row * perRow, (row + 1) * perRow);
    if (!inRow.length) break;
    const tallest = stackHeight(Math.max(...inRow.map((m) => m.length)));
    inRow.forEach((refs, col) => {
      const index = row * perRow + col;
      const x = col * (PLAN_CARD.width + BAND_PAD * 2 + PLAN_WAVE_GAP);
      const y = top + BAND_HEAD;
      refs.forEach((ref, i) => cards.push({ ref, wave: index, x, y: y + i * (PLAN_CARD.height + PLAN_CARD_GAP), state: state.get(ref)!, critical: critical.has(ref) }));
      const states = refs.map((r) => state.get(r)!);
      const ws = waveState(states);
      // Every band in a row is as tall as the tallest, so their titles line up.
      const rect = bandAround([{ x, y, width: PLAN_CARD.width, height: tallest }])!;
      waves.push({ index, row, refs, state: ws, tone: WAVE_TONE[ws], title: waveTitle(index, total), description: describeWave(states, index), rect });
    });
    const bottom = top + BAND_HEAD + tallest + BAND_PAD;
    channel.push(bottom + PLAN_ROW_GAP / 2);
    top = bottom + PLAN_ROW_GAP;
  }

  // The clear ways through the picture: the gaps between waves (every row has
  // its waves in the same columns, so a gap runs the whole height), the gaps
  // between cards (every stack is on the same grid), and the channels between rows.
  const pitch = PLAN_CARD.width + BAND_PAD * 2 + PLAN_WAVE_GAP;
  const columns = Math.min(perRow, total);
  const gapAfter = (col: number) => col * pitch + PLAN_CARD.width + BAND_PAD + (col === columns - 1 ? PLAN_EDGE_MARGIN : PLAN_WAVE_GAP / 2);
  const gapBefore = (col: number) => col * pitch - BAND_PAD - (col === 0 ? PLAN_EDGE_MARGIN : PLAN_WAVE_GAP / 2);
  const at = new Map(cards.map((c) => [c.ref, c]));
  const route = (source: string, target: string): FlowPoint[] | undefined => {
    const [from, to] = [at.get(source)!, at.get(target)!];
    const [fromRow, toRow] = [Math.floor(from.wave / perRow), Math.floor(to.wave / perRow)];
    const [fromCol, toCol] = [from.wave % perRow, to.wave % perRow];
    if (fromRow === toRow && toCol === fromCol + 1) return undefined;
    let laneY: number;
    if (fromRow !== toRow) {
      laneY = channel[Math.min(fromRow, toRow)];
    } else {
      // Level with each other and nothing standing between them: a straight line is the clear way.
      const between = members.slice(from.wave + 1, to.wave);
      if (toCol > fromCol && from.y === to.y && between.every((refs) => refs.every((r) => at.get(r)!.y !== from.y))) return undefined;
      // Otherwise along the gap beside the source card, on the side the target is.
      laneY = to.y < from.y ? from.y - PLAN_CARD_GAP / 2 : from.y + PLAN_CARD.height + PLAN_CARD_GAP / 2;
    }
    return [{ x: gapAfter(fromCol), y: laneY }, { x: gapBefore(toCol), y: laneY }];
  };

  const edges: PlanEdgeLayout[] = [];
  for (const t of tasks) {
    for (const dep of new Set(t.dependsOn)) {
      if (!byRef.has(dep) || dep === t.ref) continue;
      const from = state.get(dep)!;
      const to = state.get(t.ref)!;
      const onPath = critical.has(dep) && critical.has(t.ref);
      const kind: PlanEdgeKind = from === 'done' && to === 'done' ? 'satisfied' : stuck(to) ? 'blocked' : onPath ? 'critical' : inFlight(to) ? 'running' : 'dependency';
      const via = route(dep, t.ref);
      edges.push({ id: `${dep}->${t.ref}`, source: dep, target: t.ref, kind, critical: onPath, ...(via ? { via } : {}) });
    }
  }

  const rects = waves.map((w) => w.rect);
  const bounds = rects.length
    ? (() => {
        const left = Math.min(...rects.map((r) => r.x));
        const upper = Math.min(...rects.map((r) => r.y));
        return { x: left, y: upper, width: Math.max(...rects.map((r) => r.x + r.width)) - left, height: Math.max(...rects.map((r) => r.y + r.height)) - upper };
      })()
    : { x: 0, y: 0, width: 0, height: 0 };

  return { cards, waves, edges, rows: Math.max(1, new Set(waves.map((w) => w.row)).size), bounds, inFlight: [...state.values()].some(inFlight) };
}

/**
 * The overview earns its corner sooner here than on the other canvases
 * (flowStyle's MINIMAP_MIN_CARDS): three waves already scroll sideways in a
 * narrow pane, however few cards are on them.
 */
export function planWantsMinimap(cards: number, waves: number): boolean {
  return cards > 8 || waves > 2;
}
